import { CheckIcon } from '@heroicons/react/24/solid';
import React from 'react';
import { SharedRecommendedDesign } from '../../../services';

// #region ShareDesignCardProps
export interface ShareDesignCardProps {
    design: SharedRecommendedDesign;
    selected: boolean;
    atCapacity: boolean;
    locked: boolean;
    onToggle: (designId: number) => void;
    onPreview: (design: SharedRecommendedDesign) => void;
}

// #region ShareDesignCard
// Thumbnail card — tap image to preview, tap the select control to pick/unpick.
// Fully brand-themed and light-mode only; touch targets are at least 44px.
export const ShareDesignCard: React.FunctionComponent<ShareDesignCardProps> = (props: ShareDesignCardProps): JSX.Element => {
    const { design, selected, atCapacity, locked, onToggle, onPreview } = props;

    const selectDisabled = locked || (!selected && atCapacity);
    const title = design.title || 'Untitled';

    return (
        <div
            className={`group relative flex h-full flex-col overflow-hidden rounded-2xl bg-share-raised transition-all ${
                selected
                    ? 'shadow-md ring-2 ring-share-primary'
                    : 'shadow-sm ring-1 ring-share-border hover:shadow-md'
            }`}
        >
            <button
                type="button"
                // Fixed 4:3 (mobile, one card per row) / square (multi-column)
                // box with an absolutely-filled image, so every thumbnail is the
                // same height no matter what the source image's ratio is.
                className="relative block w-full overflow-hidden bg-share-surface aspect-[4/3] sm:aspect-square"
                onClick={() => onPreview(design)}
                aria-label={`Preview ${title}`}
            >
                {design.thumbnail_url ? (
                    <img
                        src={design.thumbnail_url}
                        alt={title}
                        loading="lazy"
                        className="absolute inset-0 h-full w-full object-cover object-center"
                        onError={(event: React.SyntheticEvent<HTMLImageElement>) => {
                            event.currentTarget.style.display = 'none';
                        }}
                    />
                ) : (
                    <div className="flex h-full w-full items-center justify-center text-xs text-share-muted">
                        No preview
                    </div>
                )}

                {selected && (
                    <span
                        className="pointer-events-none absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full shadow"
                        style={{ backgroundColor: 'var(--share-primary)', color: 'var(--share-primary-contrast)' }}
                    >
                        <CheckIcon className="h-4 w-4" />
                    </span>
                )}
            </button>

            <div className="flex flex-1 flex-col gap-2 p-3">
                <div className="truncate text-sm font-medium text-share-text" title={title}>
                    {title}
                </div>
                {!locked && (
                    <button
                        type="button"
                        onClick={() => onToggle(design.design_id)}
                        disabled={selectDisabled}
                        style={selected
                            ? { backgroundColor: 'var(--share-primary)', color: 'var(--share-primary-contrast)' }
                            : undefined}
                        className={`mt-auto min-h-[44px] w-full rounded-xl px-3 text-sm font-semibold transition-colors ${
                            selected
                                ? ''
                                : selectDisabled
                                    ? 'cursor-not-allowed bg-share-surface text-share-muted'
                                    : 'bg-share-primary-soft text-share-primary hover:brightness-95'
                        }`}
                    >
                        {selected ? 'Selected' : 'Select'}
                    </button>
                )}
            </div>
        </div>
    );
};
